// Wiring between the recorder engine's sink interface and vista's ingest API.
//
// The engine knows nothing about our endpoints: it hands byte chunks to a sink
// and expects three things — write(), finalize(), abort(). This module is that
// sink, plus the part of resume that has to live on the client: remembering
// WHICH capture was in flight when the tab died.
//
// The unusual bit versus the same sink on kids: the upload can be picked up
// again. The capture id is parked in localStorage the moment the upload opens
// and cleared only when the clip is finished or abandoned, so a reload — or a
// phone that put the browser to sleep mid-take — comes back to a screen that
// can say "у тебя есть незаконченная загрузка" instead of silently losing it.
import { VISTA_PART_BYTES, type VistaMpuPart } from '@weern/contracts';
import { API_BASE, apiPost, getToken } from '../api';

/** Survives a reload; cleared on finish/abort. One in flight at a time — the
 *  recorder can only film one take at a time anyway. */
const PENDING_KEY = 'vista.capture.pending';

export interface PendingCapture {
  captureId: string;
  taskSlug: string;
  startedAt: number;
}

export const getPendingCapture = (): PendingCapture | null => {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingCapture) : null;
  } catch {
    return null;
  }
};

const setPendingCapture = (p: PendingCapture): void => {
  localStorage.setItem(PENDING_KEY, JSON.stringify(p));
};

export const clearPendingCapture = (): void => localStorage.removeItem(PENDING_KEY);

const MAX_PART_RETRIES = 3;
/**
 * Потолок буфера незалитого. 64 МиБ — это ~110 секунд съёмки при 5 Мбит/с:
 * столько запись переживёт полностью глухую сеть, ничего не заметив. Больше
 * держать опасно (память телефона), меньше — возвращает провалы на каждой
 * заминке канала.
 */
const MAX_BUFFER_BYTES = 64 * 1024 * 1024;
const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export interface OpenArgs {
  /**
   * Короткая приписка к отпечатку устройства: какой объектив выбран и завёлся
   * ли звук. Без неё «звука нет» и «прыгает картинка» приходится выяснять
   * перепиской, а не запросом к базе (12.08 — потеряли на этом полдня).
   */
  note?: () => string;
  taskSlug: string;
  orientation: 'landscape' | 'portrait';
  staticCamera?: boolean;
  segmentIndex?: number;
  segmentOf?: string;
  /** 'upload' — готовый файл вместо записи движком (запасной путь). */
  source?: 'recorder' | 'upload';
  fileName?: string;
  contentType?: string;
}

export interface CaptureSession {
  captureId: string;
  nextPart: number;
}

/**
 * Compact device fingerprint from the recorder's own userAgent (F10). Staff
 * cannot tell clips from different phones apart otherwise — a smear or a run
 * of dropped frames is a device fact, not a person fact, and right now it is
 * invisible in the queue. Deliberately NOT a full UA dump: that would be
 * noise on screen and would push past the contract's 120-char cap; this picks
 * out the handful of facts that actually distinguish one phone from another.
 */
function parseDevice(): string {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const trunc = (s: string) => (s.length > 60 ? s.slice(0, 60) : s);

  if (/iPhone|iPad/.test(ua)) {
    const kind = /iPad/.test(ua) ? 'iPad' : 'iPhone';
    const osMatch = ua.match(/OS (\d+)[_.](\d+)/);
    const os = osMatch ? `iOS ${osMatch[1]}.${osMatch[2]}` : 'iOS';
    // Safari is the default WebKit UA; Chrome/Firefox on iOS still run on
    // WebKit but tag themselves distinctly.
    const browser = /CriOS/.test(ua) ? 'Chrome' : /FxiOS/.test(ua) ? 'Firefox' : 'Safari';
    // Версию БРАУЗЕРА берём отдельно: Apple замораживает версию системы в
    // опознавательной строке, и два разных телефона приходят как одна и та же
    // «iOS 18.7». А от версии Safari зависит, умеет ли устройство кодировать
    // звук вообще (AudioEncoder появился только в 26) — без неё разбор
    // «почему нет звука» упирается в неразличимые записи (12.08).
    const verMatch = ua.match(/Version\/(\d+)/);
    const ver = verMatch ? `/${verMatch[1]}` : '';
    return trunc(`${kind} ${os} ${browser}${ver}`);
  }

  if (/Android/.test(ua)) {
    const osMatch = ua.match(/Android (\d+(?:\.\d+)?)/);
    const os = osMatch ? `Android ${osMatch[1]}` : 'Android';
    // The model sits right before "Build/" in a standard Android UA
    // (e.g. "…; SM-G991B Build/…") — absent on some stock browsers, in which
    // case we simply fall back to platform + browser only.
    const modelMatch = ua.match(/;\s*([^;()]+?)\s+Build\//);
    const model = modelMatch ? modelMatch[1].trim() : '';
    const browser = /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : 'Browser';
    return trunc([model, os, browser].filter(Boolean).join(' '));
  }

  // Desktop / anything else (a laptop webcam take, an upload from a desktop
  // browser): platform + browser is the useful pair here, not a UA dump.
  const platform =
    typeof navigator !== 'undefined' && navigator.platform ? navigator.platform : 'Unknown';
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Chrome\//.test(ua)
      ? 'Chrome'
      : /Firefox\//.test(ua)
        ? 'Firefox'
        : /Safari\//.test(ua)
          ? 'Safari'
          : 'Browser';
  return trunc(`${platform} ${browser}`);
}

const BUILD_TAG: string = (import.meta.env.VITE_BUILD_TAG as string | undefined) ?? 'dev';

export async function openCapture(args: OpenArgs): Promise<CaptureSession> {
  const { note, ...rest } = args;
  const res = (await apiPost('/capture/open', {
    ...rest,
    device: `${parseDevice()} · ${BUILD_TAG}${note ? ` · ${note()}` : ''}`,
    recordedAt: Date.now(),
  })) as { captureId: string; nextPart: number };
  setPendingCapture({ captureId: res.captureId, taskSlug: args.taskSlug, startedAt: Date.now() });
  return { captureId: res.captureId, nextPart: res.nextPart };
}

export async function resumeCapture(captureId: string): Promise<{
  nextPart: number;
  partsUploaded: number;
  bytesReceived: number;
  expired: boolean;
}> {
  return (await apiPost('/capture/resume', { captureId })) as {
    nextPart: number;
    partsUploaded: number;
    bytesReceived: number;
    expired: boolean;
  };
}

/** Raw bytes, metadata in headers — the body is the part itself, so this one
 *  call cannot go through the JSON client.
 *
 *  onProgress — байты, УШЕДШИЕ в сеть внутри текущей части. fetch() прогресса
 *  отправки не отдаёт в принципе, поэтому здесь XHR: часть в 8 МиБ на слабом
 *  аплинке льётся десятки секунд, и «0%» всё это время читается как «зависло»
 *  (Tim, 11.08 — прогресс обновлялся только на границах частей). */
export async function putPartRaw(
  captureId: string,
  partNumber: number,
  body: Blob,
  onProgress?: (sentBytes: number) => void,
): Promise<VistaMpuPart> {
  if (!onProgress) return putPart(captureId, partNumber, body);
  const token = getToken();
  return new Promise<VistaMpuPart>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', `${API_BASE}/api/vista/capture/part`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.setRequestHeader('X-Capture-Id', captureId);
    xhr.setRequestHeader('X-Mpu-Part', String(partNumber));
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText) as VistaMpuPart);
        } catch {
          reject(new Error('part_bad_json'));
        }
      } else {
        let msg = `part_http_${xhr.status}`;
        try {
          const err = JSON.parse(xhr.responseText) as { error?: string };
          if (err.error) msg = err.error;
        } catch { /* тело не JSON — оставляем код статуса */ }
        reject(new Error(msg));
      }
    };
    xhr.onerror = () => reject(new Error('part_network'));
    xhr.send(body);
  });
}

async function putPart(captureId: string, partNumber: number, body: Blob): Promise<VistaMpuPart> {
  const token = getToken();
  const res = await fetch(`${API_BASE}/api/vista/capture/part`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/octet-stream',
      'X-Capture-Id': captureId,
      'X-Mpu-Part': String(partNumber),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body,
  });
  if (!res.ok) {
    const err = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(err?.error ?? `part_http_${res.status}`);
  }
  return (await res.json()) as VistaMpuPart;
}

export interface FinishArgs {
  captureId: string;
  durationSeconds: number;
  width: number;
  height: number;
  fps: number;
  frameCount?: number;
  wallClockSeconds?: number;
  fileName?: string;
}

export async function finishCapture(args: FinishArgs): Promise<{
  status: string;
  integrityRatio: number | null;
}> {
  const res = (await apiPost('/capture/finish', args)) as {
    status: string;
    integrityRatio: number | null;
  };
  clearPendingCapture();
  return res;
}

export interface AbortStats {
  encodedFrames: number;
  droppedFrames: number;
  wallClockSeconds: number;
  realVideoSeconds: number;
  /** Код+текст ошибки движка, приведшей к аборту — без неё разбор падения
   *  на границе блока (11.08, дважды по 15 минут) шёл вслепую. */
  clientError?: string;
  /** Состояние заливки на момент падения — подставляет сам сток, экран об
   *  этих числах не знает. */
  uploadBytesPerSec?: number;
  uploadBufferedBytes?: number;
}

const DEAD_LETTER_KEY = 'vista.abort.deadletter';

/** Недоставленный рапорт об аборте досылается при следующем старте приложения.
 *  Прогон 22:08 (a00e2554): часть залилась в 22:15, а abort до сервера не
 *  дошёл — строка зависла в 'recording', диагностика пропала, разбор встал. */
export async function flushAbortDeadLetter(): Promise<void> {
  const raw = localStorage.getItem(DEAD_LETTER_KEY);
  if (!raw) return;
  try {
    const p = JSON.parse(raw) as { captureId: string; reason: string; stats?: AbortStats };
    await apiPost('/capture/abort', p);
    localStorage.removeItem(DEAD_LETTER_KEY);
  } catch {
    /* сеть всё ещё лежит — попробуем при следующем старте */
  }
}

export async function abortCapture(
  captureId: string,
  reason: 'integrity' | 'user_cancelled' | 'error' | 'expired',
  stats?: AbortStats,
): Promise<{ salvaged: boolean }> {
  try {
    const res = (await apiPost('/capture/abort', { captureId, reason, stats })) as {
      salvaged?: boolean;
    };
    localStorage.removeItem(DEAD_LETTER_KEY);
    return { salvaged: !!res.salvaged };
  } catch {
    try {
      localStorage.setItem(DEAD_LETTER_KEY, JSON.stringify({ captureId, reason, stats }));
    } catch { /* нет места — рапорт теряем, но запись важнее */ }
    return { salvaged: false };
  } finally {
    // Even a failed abort must clear the pointer: leaving it would offer the
    // person a resume that can never work.
    clearPendingCapture();
  }
}

export interface VistaSinkOptions {
  open: OpenArgs;
  /** Progress for the UI: how much has actually landed, not how much was filmed.
   *
   *  `bufferedBytes` — снятое, но ещё не ушедшее в сеть. Именно это число
   *  объясняет «запись кончилась, а полоса ещё крутится»: на слабом аплинке
   *  хвост копится всю съёмку и доезжает уже после стопа. `bytesPerSec` —
   *  скорость по последним ушедшим частям, ноль пока ни одна не завершилась. */
  onProgress?: (info: {
    parts: number;
    uploadedBytes: number;
    bufferedBytes: number;
    bytesPerSec: number;
  }) => void;
  /** Fired once the upload exists, so the screen can show a resume affordance. */
  onOpened?: (session: CaptureSession) => void;
  /**
   * Read the recorder's numbers at the moment of an abort. The sink can't know
   * them itself (it sees bytes, not frames), and without them a rejected take
   * is undiagnosable — the server stores whatever this returns as diag_json.
   */
  getStats?: () => AbortStats | null;
}

/**
 * The sink the engine writes into. Bytes accumulate up to one part and are
 * uploaded SEQUENTIALLY — one in flight, minimum RAM, and the await inside
 * write() is what pushes back on the encoder when the network is slow. That
 * backpressure is the whole reason the memory stays flat on a twenty-minute
 * take instead of growing until iOS kills the tab.
 */
export function vistaR2Sink(opts: VistaSinkOptions) {
  // Куски от движка (Uint8Array) и недобор предыдущего флаша (Blob) —
  // всё это одинаково годится в конструктор Blob.
  let pending: BlobPart[] = [];
  let pendingBytes = 0;
  let partNumber = 0;
  let uploadedBytes = 0;
  // Скорость меряем по последним трём частям: одна даёт слишком нервную
  // цифру (часть уходит секундами), а среднее за всю съёмку прячет момент,
  // когда канал просел именно сейчас.
  const partSpeeds: number[] = [];
  const parts: VistaMpuPart[] = [];
  let session: CaptureSession | null = null;
  let aborted = false;
  // Состояние фонового насоса заливки: идёт ли он сейчас, его текущая
  // цепочка (её ждут finalize и переполненный write) и последняя ошибка.
  let pumping = false;
  let pumpChain: Promise<void> = Promise.resolve();
  let pumpError: unknown = null;

  async function ensureSession(): Promise<CaptureSession> {
    if (!session) {
      session = await openCapture(opts.open);
      // Parts are numbered by the server's expectation, not from 1: a resumed
      // upload continues where it stopped.
      partNumber = session.nextPart - 1;
      opts.onOpened?.(session);
    }
    return session;
  }

  async function putWithRetry(captureId: string, n: number, body: Blob): Promise<VistaMpuPart> {
    let lastErr: unknown;
    for (let attempt = 1; attempt <= MAX_PART_RETRIES; attempt++) {
      try {
        return await putPart(captureId, n, body);
      } catch (e) {
        lastErr = e;
        // Linear backoff: on a phone the usual cause is a moment of no signal,
        // and waiting a beat costs less than failing the take.
        if (attempt < MAX_PART_RETRIES) await delay(attempt * 500);
      }
    }
    throw lastErr;
  }

  /**
   * Отправить накопленное частями РОВНО по VISTA_PART_BYTES.
   *
   * КОРЕНЬ всех падений длинных записей 11.08: раньше уходило всё, что
   * накопилось к моменту флаша — то есть «8 МиБ + хвост последнего чанка»,
   * каждый раз своей длины. R2 на завершении отвечает
   * «All non-trailing parts must have the same length (10048)» и НЕ собирает
   * объект: любая запись длиннее одной части была обречена, а короткие
   * (одна часть) проходили — отсюда «3 секунды работают, 15 минут нет».
   *
   * `final` = закрываем поток: хвост уходит последней частью (ему одному
   * разрешено быть короче и меньше 5 МиБ-порога R2).
   *
   * ВЫЗЫВАЕТСЯ ФОНОВЫМ НАСОСОМ, не из write(). Почему — см. `pump` ниже.
   */
  async function flushPart(final = false): Promise<void> {
    if (pendingBytes === 0) return;
    const s = await ensureSession();
    let rest: Blob = new Blob(pending as BlobPart[]);
    pending = [];
    pendingBytes = 0;

    while (rest.size >= VISTA_PART_BYTES) {
      const body = rest.slice(0, VISTA_PART_BYTES);
      rest = rest.slice(VISTA_PART_BYTES);
      partNumber += 1;
      const startedAt = performance.now();
      const part = await putWithRetry(s.captureId, partNumber, body);
      noteSpeed(body.size, performance.now() - startedAt);
      parts.push(part);
      uploadedBytes += body.size;
      report();
    }

    if (rest.size === 0) return;
    if (!final) {
      // Недобор до полной части ждёт следующих кадров. Кладём ВПЕРЁД: пока
      // часть уходила, write() мог дописать новые куски, и порядок байт в
      // файле обязан остаться исходным.
      pending.unshift(rest);
      pendingBytes += rest.size;
      return;
    }
    partNumber += 1;
    const startedAt = performance.now();
    const tail = await putWithRetry(s.captureId, partNumber, rest);
    noteSpeed(rest.size, performance.now() - startedAt);
    parts.push(tail);
    uploadedBytes += rest.size;
    report();
  }

  function noteSpeed(bytes: number, ms: number): void {
    if (ms <= 0) return;
    partSpeeds.push((bytes * 1000) / ms);
    if (partSpeeds.length > 3) partSpeeds.shift();
  }

  /** Сетевые числа к статистике падения: экран отдаёт кадры, сток — канал. */
  function withNetStats(st: AbortStats | null): AbortStats | null {
    if (!st) return st;
    const bytesPerSec = partSpeeds.length
      ? partSpeeds.reduce((a, b) => a + b, 0) / partSpeeds.length
      : 0;
    return { ...st, uploadBytesPerSec: Math.round(bytesPerSec), uploadBufferedBytes: pendingBytes };
  }

  function report(): void {
    const bytesPerSec = partSpeeds.length
      ? partSpeeds.reduce((a, b) => a + b, 0) / partSpeeds.length
      : 0;
    opts.onProgress?.({ parts: partNumber, uploadedBytes, bufferedBytes: pendingBytes, bytesPerSec });
  }

  /**
   * Фоновый насос заливки.
   *
   * Раньше write() ЖДАЛ отправки части — и через муксер это торможение
   * доходило до энкодера, который на время заливки отбрасывал кадры. Разбор
   * боевого 20-минутного блока: **87 провалов, ровно с ритмом заливки —
   * медиана 14 секунд между ними, каждый по 1–5 секунд без единого кадра**.
   * Половина клипа при этом идёт честные 30 fps. Именно это Tim увидел как
   * «как будто 3-5 fps, жопа полная» — и был прав: 24 fps сами по себе
   * смотрятся нормально, а вот секундные замирания каждые 14 секунд — нет.
   *
   * Теперь write() только копит, а отправка идёт параллельно. Обратное
   * давление никуда не делось, но включается ПОЗЖЕ: пока в буфере меньше
   * MAX_BUFFER_BYTES, съёмка не должна замечать сеть вовсе.
   */
  function pump(): void {
    if (pumping) return;
    pumping = true;
    pumpChain = (async () => {
      try {
        while (!aborted && pendingBytes >= VISTA_PART_BYTES) await flushPart(false);
      } catch (e) {
        // Ошибку не глотаем: её увидит finalize/abort, а до тех пор запись
        // продолжается — материал важнее рапорта.
        pumpError = e;
      } finally {
        pumping = false;
      }
    })();
  }

  return {
    /** The engine's own handle on the capture, once it exists. */
    get captureId(): string | null {
      return session?.captureId ?? null;
    },
    async write(chunk: Uint8Array): Promise<void> {
      if (aborted) return;
      if (pumpError) throw pumpError;
      // Копия в собственный ArrayBuffer: движок переиспользует свои буферы, а
      // Blob соберётся позже — держать ссылку на чужую память нельзя.
      pending.push(new Uint8Array(chunk).buffer as ArrayBuffer);
      pendingBytes += chunk.byteLength;
      if (pendingBytes >= VISTA_PART_BYTES) pump();
      // Ждём, только когда буфер реально распух: сеть встала совсем, и
      // складывать дальше — значит съесть память телефона. До этого момента
      // съёмка идёт, не оглядываясь на заливку.
      while (!aborted && pendingBytes >= MAX_BUFFER_BYTES) {
        await pumpChain;
        if (pendingBytes >= MAX_BUFFER_BYTES) await delay(250);
      }
    },
    /**
     * The engine's finalize: flush the tail part (the last one is allowed under
     * R2's 5 MiB floor) and stop there. Completing the multipart is deliberately
     * NOT done here — that happens in /capture/finish, which also carries the
     * frame counts the integrity gate is computed from. Splitting them keeps a
     * clip from being sealed before it has been judged.
     */
    async finalize(): Promise<void> {
      // Дожидаемся фонового насоса, потом дожимаем хвост.
      await pumpChain;
      if (pumpError) throw pumpError;
      if (!aborted) await flushPart(true);
    },
    async abort(reason: 'integrity' | 'user_cancelled' | 'error' = 'error'): Promise<void> {
      // Runtime callers include the muxer, which forwards arbitrary reasons
      // ("dispose", Error objects). Anything non-technical would 400 on the
      // server and leave the row stuck in 'recording' — normalize first.
      if (reason !== 'integrity' && reason !== 'user_cancelled') reason = 'error';
      if (aborted) return; // double abort (engine + screen) must not re-fire
      aborted = true;
      // Technical failures salvage server-side: flush the tail part first so
      // the sealed file is as long as possible (the last part may be under
      // R2's 5 MiB floor — allowed). A deliberate cancel just drops it.
      if (reason !== 'user_cancelled' && session && pendingBytes > 0) {
        try {
          await flushPart(true);
        } catch {
          /* the tail is a bonus, not a requirement */
        }
      }
      pending = [];
      pendingBytes = 0;
      if (session)
        await abortCapture(
          session.captureId,
          reason,
          withNetStats(opts.getStats?.() ?? null) ?? undefined,
        );
    },
  };
}
